// src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Link
} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import {
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  CssBaseline,
  GlobalStyles,
  Box
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import GroupsIcon from "@mui/icons-material/Groups";
import TimelineIcon from "@mui/icons-material/Timeline";

// Component Imports
import Home from "./components/Home.jsx";
import Search from "./components/Search.jsx";
import BookDetail from "./components/BookDetail.jsx";
import BookSearchDetail from './components/BookSearchDetail';
import AddBook from "./components/AddBook.jsx";
import MyBooks from "./components/MyBooks.jsx";
import AddHighlight from "./components/AddHighlight.jsx";
import EditHighlight from "./components/EditHighlight.jsx";
import MyPage from "./components/MyPage.jsx";
import HighlightCreatePage from './components/HighlightCreatePage';
// PWAInstallBanner import 주석처리 - 바로가기 추가 메시지 비활성화
// import PWAInstallBanner from './components/PWAInstallBanner';
import NicknameEditPage from './pages/NicknameEditPage';
import AboutPage from './pages/AboutPage';
import BioEditPage from './pages/BioEditPage';
import BookMemoEdit from './components/BookMemoEdit';
import HighlightExportPage from './pages/HighlightExportPage';
import PrivacyPage from './pages/PrivacyPage';
import GameCreate from './components/GameCreate';
import GamePlay from './components/GamePlay';
// 모임 관련 컴포넌트
import ClubsList from './components/clubs/ClubsList';
import ClubCreate from './components/clubs/ClubCreate';
import ClubDetail from './components/clubs/ClubDetail';
import ClubEdit from './components/clubs/ClubEdit';
// 타임라인 컴포넌트
import TimelinePage from './components/TimelinePage';

// Firebase
import { db, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

// MUI Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#FFD700',
      light: '#FFE55C',
      dark: '#FFC107',
    },
    secondary: {
      main: '#333333',
      light: '#666666',
      dark: '#000000',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#D32F2F',
    },
  },
  typography: {
    fontFamily: 'NanumSquareR, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    button: {
      fontFamily: 'NanumSquareB, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  components: {
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: '56px',
          backgroundColor: 'transparent',
          borderTop: 'none',
          '& .MuiBottomNavigationAction-root.Mui-selected': {
            '& .MuiBottomNavigationAction-label': {
              fontFamily: 'NanumSquareB, sans-serif',
              fontWeight: 700,
              fontSize: '11px',
              color: '#333333',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'translateY(-1px)',
            },
            '& .MuiSvgIcon-root': {
              color: '#FFD700',
              transform: 'scale(1.1) translateY(-1px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3))',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '20px',
              height: '2px',
              background: 'linear-gradient(90deg, #FFD700 0%, #FFE55C 100%)',
              borderRadius: '1px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px rgba(255, 215, 0, 0.3)',
            },
          },
          '& .MuiBottomNavigationAction-root': {
            color: '#999999',
            minWidth: 'auto',
            paddingTop: '8px',
            paddingBottom: '8px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            '&:hover': {
              backgroundColor: 'rgba(255, 215, 0, 0.08)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              },
              '& .MuiBottomNavigationAction-label': {
                transform: 'translateY(-1px)',
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            },
            '&:active': {
              transform: 'scale(0.95)',
              transition: 'transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor: 'rgba(255, 215, 0, 0.12)',
            },
            '& .MuiBottomNavigationAction-label': {
              fontFamily: 'NanumSquareR, sans-serif',
              fontWeight: 500,
              fontSize: '10px',
              marginTop: '2px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '22px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          },
          // MUI 기본 인디케이터 완전 비활성화
          '& .MuiBottomNavigationAction-root.Mui-selected::before': {
            display: 'none !important',
          },
          '& .MuiBottomNavigationAction-root.Mui-selected::after': {
            display: 'none !important',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

function AppContent() {
  const [value, setValue] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  const handleNavigation = (path, navValue) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path);
      setValue(navValue);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/') { setValue(0); }
    else if (path === '/search') { setValue(1); }
    else if (path === '/timeline') { setValue(2); }
    else if (path.startsWith('/clubs')) { setValue(3); }
    else if (path === '/mypage' || path.startsWith('/u/')) { setValue(4); }
  }, [location.pathname]);

  const hideBottomNavPaths = ['/highlight/create/', '/highlight/edit/', '/export/highlight', '/mypage/bio-edit', '/mypage/nickname-edit', '/game/create', '/game/play/'];
  const shouldHideBottomNav = hideBottomNavPaths.some(path => location.pathname.startsWith(path)) || 
    // 비로그인 상태에서 MyPage 접근 시 하단 네비게이션 숨김
    (location.pathname === '/mypage' && !currentUser);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      paddingBottom: shouldHideBottomNav ? 0 : '56px',
      maxWidth: '600px',
      margin: '0 auto',
      width: '100%'
    }}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/u/:userId" element={<MyPage />} />
        <Route path="/booksearchdetail/:isbn" element={<BookSearchDetail />} />
        <Route path="/book/:id" element={<BookDetail />} />
        <Route path="/book/:bookId/memo-edit" element={<BookMemoEdit />} />
        <Route path="/add-book" element={<AddBook />} />
        <Route path="/my-books" element={<MyBooks />} />
        <Route path="/highlight/add" element={<AddHighlight />} />
        <Route path="/highlight/edit/:id" element={<EditHighlight />} />
        <Route path="/highlight/create/:bookId" element={<HighlightCreatePage />} />
        <Route path="/export/highlight" element={<HighlightExportPage />} />
        <Route path="/mypage/nickname-edit" element={<NicknameEditPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/mypage/bio-edit" element={<BioEditPage />} />
        <Route path="/game/create" element={<GameCreate />} />
        <Route path="/game/play/:testId" element={<GamePlay />} />
        {/* 모임 관련 라우트 */}
        <Route path="/clubs" element={<ClubsList />} />
        <Route path="/clubs/new" element={<ClubCreate />} />
        <Route path="/clubs/:id" element={<ClubDetail />} />
        <Route path="/clubs/:id/edit" element={<ClubEdit />} />
        {/* 타임라인 라우트 */}
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {!shouldHideBottomNav && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.96) 50%, rgba(255,255,255,0.98) 100%)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            maxWidth: '600px',
            margin: '0 auto',
            width: '100%',
            zIndex: 1000,
            borderTop: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12), 0 -2px 8px rgba(0,0,0,0.08)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.3) 50%, transparent 100%)',
            },
          }}
          elevation={0}
        >
          <BottomNavigation
            value={value}
            showLabels
          >
            <BottomNavigationAction 
              label="홈" 
              icon={<DashboardIcon />} 
              onClick={() => handleNavigation('/', 0)}
            />
            <BottomNavigationAction 
              label="검색" 
              icon={<SearchRoundedIcon />} 
              onClick={() => handleNavigation('/search', 1)}
            />
            <BottomNavigationAction 
              label="타임라인" 
              icon={<TimelineIcon />} 
              onClick={() => handleNavigation('/timeline', 2)}
            />
            <BottomNavigationAction 
              label="모임" 
              icon={<GroupsIcon />} 
              onClick={() => handleNavigation('/clubs', 3)}
            />
            <BottomNavigationAction 
              label="내정보" 
              icon={<AccountCircleIcon />} 
              onClick={() => {
                const myPagePath = currentUser ? `/u/${currentUser.uid}` : "/mypage";
                handleNavigation(myPagePath, 4);
              }}
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}

function NotFound() {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <h2 style={{ marginBottom: '10px' }}>Page Not Found</h2>
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Oops! The page you're looking for doesn't seem to exist.
      </p>
      <Link to="/" style={{ color: 'inherit', textDecoration: 'underline' }}>
        Go Back Home
      </Link>
    </div>
  );
}

function App() {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(() => {
            console.log('Service Worker registered successfully');
          })
          .catch((error) => {
            console.log('Service Worker registration failed', error);
          });
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{}} />
        <Router>
          <AppContent />
        </Router>
        {/* PWAInstallBanner 주석처리 - 바로가기 추가 메시지 비활성화 */}
        {/* <PWAInstallBanner /> */}
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
